import { useState, useEffect } from 'react';
import { API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Edit3, Trash2, Save } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

function ContentManagement() {
  const [sopContent, setSopContent] = useState('');
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showFaqDialog, setShowFaqDialog] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [faqForm, setFaqForm] = useState({ question: '', answer: '', category: '' });

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const [sopRes, faqRes] = await Promise.all([
        axios.get(`${API}/customer-sop`),
        axios.get(`${API}/faqs`)
      ]);
      setSopContent(sopRes.data.content || '');
      setFaqs(faqRes.data.faqs || []);
    } catch (error) {
      toast.error('Failed to fetch content');
    } finally {
      setLoading(false);
    }
  };

  const saveSOP = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/customer-sop`, { content: sopContent });
      toast.success('Customer SOP updated successfully!');
    } catch (error) {
      toast.error('Failed to update SOP');
    } finally {
      setSaving(false);
    }
  };

  const openFaqDialog = (faq = null) => {
    if (faq) {
      setEditingFaq(faq);
      setFaqForm({ question: faq.question, answer: faq.answer, category: faq.category });
    } else {
      setEditingFaq(null);
      setFaqForm({ question: '', answer: '', category: 'General' });
    }
    setShowFaqDialog(true);
  };

  const saveFaq = async () => {
    if (!faqForm.question || !faqForm.answer || !faqForm.category) {
      toast.error('Please fill all fields');
      return;
    }

    setSaving(true);
    try {
      let updatedFaqs;
      if (editingFaq) {
        updatedFaqs = faqs.map(f => f.id === editingFaq.id ? { ...f, ...faqForm } : f);
      } else {
        updatedFaqs = [...faqs, { ...faqForm, id: Date.now().toString(), order: faqs.length }];
      }
      
      await axios.put(`${API}/faqs`, { faqs: updatedFaqs });
      setFaqs(updatedFaqs);
      setShowFaqDialog(false);
      toast.success(editingFaq ? 'FAQ updated!' : 'FAQ added!');
    } catch (error) {
      toast.error('Failed to save FAQ');
    } finally {
      setSaving(false);
    }
  };

  const deleteFaq = async (faqId) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) return;

    try {
      const updatedFaqs = faqs.filter(f => f.id !== faqId);
      await axios.put(`${API}/faqs`, { faqs: updatedFaqs });
      setFaqs(updatedFaqs);
      toast.success('FAQ deleted!');
    } catch (error) {
      toast.error('Failed to delete FAQ');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Tabs defaultValue="sop">
        <TabsList>
          <TabsTrigger value="sop">Customer SOP</TabsTrigger>
          <TabsTrigger value="faqs">FAQs</TabsTrigger>
        </TabsList>

        <TabsContent value="sop" className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Customer Interaction SOP</h2>
              <p className="text-sm text-gray-600 mt-1">Edit customer service guidelines (HTML supported)</p>
            </div>
            <Button onClick={saveSOP} disabled={saving} className="bg-primary hover:bg-primary-light">
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <textarea
              value={sopContent}
              onChange={(e) => setSopContent(e.target.value)}
              className="w-full h-96 px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary font-mono text-sm"
              placeholder="Enter SOP content (HTML supported)..."
            />
          </div>
        </TabsContent>

        <TabsContent value="faqs" className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">FAQs Management</h2>
              <p className="text-sm text-gray-600 mt-1">Add, edit, or remove frequently asked questions</p>
            </div>
            <Button onClick={() => openFaqDialog()} className="bg-primary hover:bg-primary-light">
              <Plus className="h-4 w-4 mr-2" />
              Add FAQ
            </Button>
          </div>

          {faqs.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-xl shadow-sm border border-gray-200">
              <p className="text-gray-500">No FAQs yet. Click &quot;Add FAQ&quot; to create one.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {faqs.map((faq) => (
                <div key={faq.id} className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-primary/10 text-primary">
                        {faq.category}
                      </span>
                      <h3 className="font-semibold text-gray-900 mt-2">{faq.question}</h3>
                      <p className="text-sm text-gray-600 mt-1">{faq.answer}</p>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <Button variant="ghost" size="sm" onClick={() => openFaqDialog(faq)}>
                        <Edit3 className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteFaq(faq.id)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* FAQ Dialog */}
      <Dialog open={showFaqDialog} onOpenChange={setShowFaqDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingFaq ? 'Edit FAQ' : 'Add New FAQ'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Category</Label>
              <Input
                value={faqForm.category}
                onChange={(e) => setFaqForm({ ...faqForm, category: e.target.value })}
                placeholder="e.g., General, Acai Bowls, Smoothies"
              />
            </div>
            <div>
              <Label>Question</Label>
              <Input
                value={faqForm.question}
                onChange={(e) => setFaqForm({ ...faqForm, question: e.target.value })}
                placeholder="Enter question..."
              />
            </div>
            <div>
              <Label>Answer</Label>
              <textarea
                value={faqForm.answer}
                onChange={(e) => setFaqForm({ ...faqForm, answer: e.target.value })}
                className="w-full h-32 px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Enter answer..."
              />
            </div>
            <Button onClick={saveFaq} disabled={saving} className="w-full">
              {saving ? 'Saving...' : 'Save FAQ'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ContentManagement;